import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { API_BASE_URL as API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Mail, Headphones, Users, Briefcase, Newspaper, Scale, 
  Send, Clock, CheckCircle, Loader2, AlertTriangle
} from 'lucide-react';


const CONTACT_CATEGORIES = [
  { value: 'support', label: 'Support', icon: Headphones, email: 'support@dmvsoundstage.com' },
  { value: 'artist', label: 'Artist Question', icon: Users, email: 'artists@dmvsoundstage.com' },
  { value: 'partnership', label: 'Partnership', icon: Briefcase, email: 'partners@dmvsoundstage.com' },
  { value: 'press', label: 'Press', icon: Newspaper, email: 'press@dmvsoundstage.com' },
  { value: 'legal', label: 'Legal', icon: Scale, email: 'legal@dmvsoundstage.com' },
  { value: 'other', label: 'Other', icon: Mail, email: 'support@dmvsoundstage.com' }
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/api/support/ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        const data = await response.json();
        setTicketId(data.ticket_id);
        toast.success('Message sent successfully!');
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen py-12 px-4" data-testid="contact-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-secondary/20 text-secondary">Get in Touch</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-4">
            Contact DMV Soundstage
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Have a question, partnership idea, or need support? DMV Soundstage is here to help 
            artists, creators, and partners across the DMV. Use the appropriate contact option 
            below and our team will get back to you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Info Cards */}
          <div className="space-y-4">
            {/* General Support */}
            <Card className="glass-card border-border p-5" data-testid="contact-support">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold uppercase mb-1">General Support</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    For account issues, subscription questions, profile help, or technical support.
                  </p>
                  <a href="mailto:support@dmvsoundstage.com" className="text-secondary text-sm hover:underline flex items-center gap-1">
                    <Mail className="w-3 h-3" /> support@dmvsoundstage.com
                  </a>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Response within 24–48 business hours
                  </p>
                </div>
              </div>
            </Card>

            {/* Artists & Creators */}
            <Card className="glass-card border-border p-5" data-testid="contact-artists">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-5 h-5 text-secondary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold uppercase mb-1">Artists & Creators</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Questions about uploading music, charts eligibility, verification badges, or creator features.
                  </p>
                  <a href="mailto:artists@dmvsoundstage.com" className="text-secondary text-sm hover:underline flex items-center gap-1">
                    <Mail className="w-3 h-3" /> artists@dmvsoundstage.com
                  </a>
                </div>
              </div>
            </Card>

            {/* Partnerships */}
            <Card className="glass-card border-border p-5" data-testid="contact-partnerships">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-5 h-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold uppercase mb-1">Partnerships & Business</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Interested in partnerships, sponsorships, studio listings, media collaborations, or platform integrations.
                  </p>
                  <a href="mailto:partners@dmvsoundstage.com" className="text-secondary text-sm hover:underline flex items-center gap-1">
                    <Mail className="w-3 h-3" /> partners@dmvsoundstage.com
                  </a>
                </div>
              </div>
            </Card>

            {/* Press */}
            <Card className="glass-card border-border p-5" data-testid="contact-press">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Newspaper className="w-5 h-5 text-purple-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold uppercase mb-1">Press & Public Relations</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    For interviews, press coverage, announcements, and media requests.
                  </p>
                  <a href="mailto:press@dmvsoundstage.com" className="text-secondary text-sm hover:underline flex items-center gap-1">
                    <Mail className="w-3 h-3" /> press@dmvsoundstage.com
                  </a>
                </div>
              </div>
            </Card>

            {/* Legal */}
            <Card className="glass-card border-border p-5" data-testid="contact-legal">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Scale className="w-5 h-5 text-amber-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold uppercase mb-1">Legal & Copyright</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    For legal inquiries, DMCA notices, or copyright concerns.
                  </p>
                  <a href="mailto:legal@dmvsoundstage.com" className="text-secondary text-sm hover:underline flex items-center gap-1">
                    <Mail className="w-3 h-3" /> legal@dmvsoundstage.com
                  </a>
                </div>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <div>
            {ticketId ? (
              <Card className="glass-card border-green-500/30 p-8 bg-green-500/5 text-center" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="font-heading text-2xl font-bold uppercase mb-2">Message Sent!</h2>
                <p className="text-muted-foreground mb-4">
                  Thank you for reaching out. Our team will respond within 24–48 business hours.
                </p>
                <div className="p-4 rounded-lg bg-muted/30 border border-border mb-6">
                  <p className="text-sm text-muted-foreground mb-1">Your Ticket ID:</p>
                  <p className="font-mono text-lg text-secondary font-bold">{ticketId}</p>
                  <p className="text-xs text-muted-foreground mt-2">Please save this for reference</p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => setTicketId(null)}
                  className="border-border"
                >
                  Send Another Message
                </Button>
              </Card>
            ) : (
              <Card className="glass-card border-border p-6" data-testid="contact-form">
                <h2 className="font-heading text-xl font-bold uppercase mb-6 flex items-center gap-2">
                  <Send className="w-5 h-5 text-secondary" />
                  Send a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name <span className="text-red-400">*</span></Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your full name"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address <span className="text-red-400">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.com"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject <span className="text-red-400">*</span></Label>
                    <Select 
                      value={form.subject} 
                      onValueChange={(value) => setForm({ ...form, subject: value })}
                    >
                      <SelectTrigger data-testid="contact-subject-select">
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message <span className="text-red-400">*</span></Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="How can we help you?"
                      rows={5}
                      required
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-secondary hover:bg-secondary/90"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>

                {/* Disclaimer */}
                <div className="mt-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <p className="text-xs text-amber-400 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    DMV Soundstage does not accept unsolicited music submissions via email. 
                    All music must be submitted through official DSP links within the platform.
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
